"use client";

import { publish } from "@ionic/portals";
import type { PortalMessage } from "@ionic/portals";

// Our E2E tests run against a real environment (preview) in a blackbox manner.
// This means that we can't easily mock functions, as we could in a unit test
// (whitebox). To work around this, we attach a spy function on the window, in
// this case to validate SDK events. Outside of testing, we fallback to the
// actual function.
const SPY_ALLOWED_ENVIRONMENTS = ["development", "preview"];

// Local development has a publish function on the window object.
if (
  typeof window !== "undefined" &&
  !process.env.VERCEL &&
  !(window as any).publish
) {
  (window as any).publish = publish;
}

/**
 * Publishes an event using Ionic Portals pubsub mechanism.
 *
 * This function is intended for client-side use. If \`window\` is not defined (e.g., in SSR),
 * it will log a warning and not dispatch the event.
 *
 */
const publishEventToSDK = (message: PortalMessage) => {
  if (typeof window === "undefined") {
    throw new Error("publishEventToSDK is not available in SSR");
  }

  const isSpyAllowed =
    !process.env.VERCEL ||
    SPY_ALLOWED_ENVIRONMENTS.includes(process.env.VERCEL_TARGET_ENV ?? "");

  // Use the spy if spy is allowed
  const publishFunction = isSpyAllowed ? (window as any).publish : publish;

  // send the message to the SDK
  publishFunction(message);

  switch (message.topic) {
    case "error":
      console.error(
        "Published error event. Details:",
        (message.data as any)?.error ?? message.data
      );
      break;
    default:
      // Logs the entire message object for other topics.
      console.info(`Published event: ${JSON.stringify(message)}`);
      break;
  }
};

export default publishEventToSDK;
